import { Icon, MenuItem } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useUserMutation } from 'api/users/users.api'
import i18n from 'i18next'
import React, { ReactElement, useState } from 'react'
import { Button } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'

import ProfileMenuItem from '../ProfileMenuItem/ProfileMenuItem'
import styles from './LanguagePicker.module.less'

type Language = {
  code: LanguageCode
  label: string
}

export const languages: Language[] = [
  { code: 'en', label: 'English' },
  { code: 'sv', label: 'Svenska' },
  { code: 'nb', label: 'Norsk (bokmål)' },
  { code: 'fi', label: 'Suomi' },
  { code: 'de', label: 'Deutsch' },
]

type LanguagePickerProps = {
  disabled?: boolean
}

export const changeLanguage = async (lng: LanguageCode, user: any, updateUser: any): Promise<void> => {
  await i18n.changeLanguage(lng)
  localStorage.setItem('language', lng)

  if (user) {
    user.settings = { ...user.settings, language: lng }
    await updateUser({ id: user.id, data: user })
  }

  // TODO: Fix needed. Force reload to apply new language for the dates
  setTimeout(() => {
    location.reload()
  }, 500)
}

function LanguagePicker({ disabled }: LanguagePickerProps): ReactElement {
  const { user, impersonatingUser } = useAuth()
  const { mutateAsync: updateUser } = useUserMutation()
  const [languageSelected, setLanguageSelected] = useState(i18n.language)

  const handleChangeLanguage = async (lng: LanguageCode): Promise<void> => {
    await changeLanguage(lng, impersonatingUser ?? user, updateUser)
    setLanguageSelected(lng)
  }

  return (
    <div className={styles.ProfileMenuItem_Root_Expanded}>
      {languages.map((language) => (
        <Button
          disabled={!!disabled}
          primary={languageSelected === language.code}
          secondary={languageSelected !== language.code}
          key={language.code}
          className={styles.ProfileMenuItem_Item_Expanded}
          onClick={() => (languageSelected !== language.code ? handleChangeLanguage(language.code) : null)}
        >
          {language.label}
        </Button>
      ))}
    </div>
  )
}

export default LanguagePicker

type ProfileMenuLanguagePickerProps = {
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}

export function ProfileMenuLanguagePicker({ isOpen, onToggle, onClose }: ProfileMenuLanguagePickerProps): ReactElement {
  const { user } = useAuth()
  const { mutateAsync: updateUser } = useUserMutation()

  const currentLanguage = languages.find((lang) => lang.code === i18n.language)

  const handleLanguageChange = async (lng: LanguageCode): Promise<void> => {
    await changeLanguage(lng, user, updateUser)
    onClose()
  }

  return (
    <>
      {/* Language Toggle Item */}
      <ProfileMenuItem
        icon="fal fa-globe-europe"
        onClick={(e?: React.MouseEvent) => {
          e?.stopPropagation()
          onToggle()
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Grid container direction="row" alignItems="center">
          <Grid style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
            <span>{currentLanguage?.label || 'Language'}</span>
            <Icon
              className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}
              style={{ marginLeft: '8px', fontSize: '16px' }}
            />
          </Grid>
        </Grid>
      </ProfileMenuItem>

      {/* Language Options */}
      {isOpen &&
        languages
          .filter((language) => language.code !== i18n.language)
          .map((language) => (
            <MenuItem
              key={language.code}
              style={{ padding: '8px 16px', fontSize: '14px', marginLeft: '48px' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleLanguageChange(language.code)
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  handleLanguageChange(language.code)
                }
              }}
              tabIndex={0}
            >
              {language.label}
            </MenuItem>
          ))}
    </>
  )
}
// Type definition for LanguageCode
